import React, { createContext, useContext, useEffect, useState } from 'react';
import { setArtistIdFromUser, getArtistData } from '../state/ArtistManager.js';
import CognitoAuthService from '../services/CognitoAuthService.js';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('');
  const [artistData, setArtistData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated via Cognito
    const checkAuthStatus = async () => {
      try {
        console.log('🔍 Checking authentication status...');
        
        // First, check for stored token and validate it
        const storedToken = localStorage.getItem('cognito_token');
        if (storedToken) {
          console.log('📱 Found stored token, validating...');
          
          try {
            // Decode and check token expiration
            const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            
            if (tokenPayload.exp && tokenPayload.exp > currentTime) {
              console.log('✅ Stored token is valid, restoring session...');
              
              const email = tokenPayload.email;
              
              // Set up user data
              await setArtistIdFromUser();
              
              // Map email to artist ID
              const getUserMapping = (email) => {
                const mapping = {
                  'rdv@decodedmusic.com': { artistId: 'rdv01', name: 'rue_de_vivre' },
                  'ops@decodedmusic.com': { artistId: 'ops01', name: 'operations' },
                  'info@hoodiepeeps.com': { artistId: 'dealer01', name: 'dealer' },
                  'test@decodedmusic.com': { artistId: 'test01', name: 'testuser' }
                };
                return mapping[email] || { artistId: 'rdv01', name: 'rue_de_vivre' };
              };
              
              const userMapping = getUserMapping(email);
              
              // Set user state
              setUser({ artistId: userMapping.artistId, name: userMapping.name, email });
              setUsername(userMapping.name);
              setArtistData({ id: userMapping.artistId, name: userMapping.name, email });
              setIsAuthenticated(true);
              
              console.log('🎯 Session restored successfully:', userMapping.name);
              setLoading(false);
              return;
            } else {
              console.log('⏰ Stored token expired, clearing...');
              localStorage.removeItem('cognito_token');
            }
          } catch (tokenError) {
            console.error('❌ Error validating stored token:', tokenError);
            localStorage.removeItem('cognito_token');
          }
        }
        
        // Then check if there's a token in the URL hash (from implicit flow)
        const urlHash = window.location.hash;
        if (urlHash.includes('id_token=')) {
          console.log('Found Cognito token in URL, processing...');
          
          // Extract the token from URL
          const tokenMatch = urlHash.match(/id_token=([^&]+)/);
          if (tokenMatch) {
            const token = tokenMatch[1];
            
            // Store the token
            localStorage.setItem('cognito_token', token);
            
            // Clean the URL
            window.history.replaceState(null, '', window.location.pathname);
            
            // Decode the token to get user info
            try {
              const tokenPayload = JSON.parse(atob(token.split('.')[1]));
              const email = tokenPayload.email;
              
              console.log('Successfully authenticated via Cognito Hosted UI:', email);
              
              // Set up user data
              await setArtistIdFromUser();
              
              // Map email to artist ID
              const getUserMapping = (email) => {
                const mapping = {
                  'rdv@decodedmusic.com': { artistId: 'rdv01', name: 'rue_de_vivre' },
                  'ops@decodedmusic.com': { artistId: 'ops01', name: 'operations' },
                  'info@hoodiepeeps.com': { artistId: 'dealer01', name: 'dealer' },
                  'test@decodedmusic.com': { artistId: 'test01', name: 'testuser' }
                };
                return mapping[email] || { artistId: 'rdv01', name: 'rue_de_vivre' };
              };
              
              const userMapping = getUserMapping(email);
              
              // Set user state
              setUser({ artistId: userMapping.artistId, name: userMapping.name, email });
              setUsername(userMapping.name);
              setArtistData({ id: userMapping.artistId, name: userMapping.name, email });
              setIsAuthenticated(true);
              
              console.log('🎯 Authentication successful:', userMapping.name);
              console.log('🔍 Debug - Authentication state after OAuth:', {
                isAuthenticated: true,
                user: { artistId: userMapping.artistId, name: userMapping.name, email },
                username: userMapping.name,
                artistData: { id: userMapping.artistId, name: userMapping.name, email }
              });
              setLoading(false);
              return;
            } catch (error) {
              console.error('Error decoding token:', error);
            }
          }
        }
        
        // Check for OAuth authorization code (authorization code flow)
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
          console.log('Found OAuth code, exchanging for tokens...');
          
          try {
            const tokenResponse = await fetch('https://auth.decodedmusic.com/oauth2/token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: '354arlmd5u14k3td9qn9pfg54b',
                code: code,
                redirect_uri: window.location.origin.includes('www.decodedmusic.com') 
                  ? 'https://decodedmusic.com/callback'
                  : window.location.origin + '/callback',
              }),
            });

            if (tokenResponse.ok) {
              const tokens = await tokenResponse.json();
              
              // Store tokens
              localStorage.setItem('cognito_token', tokens.id_token);
              
              // Decode token to get user info
              const tokenPayload = JSON.parse(atob(tokens.id_token.split('.')[1]));
              const email = tokenPayload.email;
              
              console.log('Successfully authenticated via OAuth:', email);
              
              // Set up user data
              await setArtistIdFromUser();
              
              // Map email to artist ID
              const getUserMapping = (email) => {
                const mapping = {
                  'rdv@decodedmusic.com': { artistId: 'rdv01', name: 'rue_de_vivre' },
                  'ops@decodedmusic.com': { artistId: 'ops01', name: 'operations' },
                  'info@hoodiepeeps.com': { artistId: 'dealer01', name: 'dealer' },
                  'test@decodedmusic.com': { artistId: 'test01', name: 'testuser' }
                };
                return mapping[email] || { artistId: 'rdv01', name: 'rue_de_vivre' };
              };
              
              const userMapping = getUserMapping(email);
              
              // Set user state
              setUser({ artistId: userMapping.artistId, name: userMapping.name, email });
              setUsername(userMapping.name);
              setArtistData({ id: userMapping.artistId, name: userMapping.name, email });
              setIsAuthenticated(true);
              
              console.log('Authentication state set:', {
                isAuthenticated: true,
                user: { artistId: userMapping.artistId, name: userMapping.name, email },
                username: userMapping.name
              });
              
              console.log('🔍 Debug - OAuth Code Flow Authentication complete:', {
                isAuthenticated: true,
                user: { artistId: userMapping.artistId, name: userMapping.name, email },
                username: userMapping.name,
                artistData: { id: userMapping.artistId, name: userMapping.name, email }
              });
              
              // Authentication complete, let callback component handle navigation
              setLoading(false);
              return;
            } else {
              const errorData = await tokenResponse.json();
              console.error('Token exchange failed:', tokenResponse.status, errorData);
            }
          } catch (tokenError) {
            console.error('Token exchange failed:', tokenError);
          }
        }

        
        // If no URL token, check for existing session
        const userResult = await CognitoAuthService.getCurrentUser();
        if (userResult.success && userResult.user) {
          const email = userResult.user;
          
          // Set up user data based on Cognito authentication
          await setArtistIdFromUser();
          
          // Fetch artist data based on email
          try {
            const artistResult = await getArtistData(null, email);
            if (artistResult && artistResult.artist) {
              const artist = artistResult.artist;
              setUser({ artistId: artist.id, name: artist.name, email });
              setUsername(artist.name);
              setArtistData(artist);
            } else {
              // Fallback to default if artist not found
              setUser({ artistId: 'rdv01', name: 'rue_de_vivre', email });
              setUsername('rue_de_vivre');
              setArtistData({ id: 'rdv01', name: 'rue_de_vivre', email });
            }
          } catch (artistError) {
            console.warn('Could not fetch artist data, using defaults:', artistError);
            setUser({ artistId: 'rdv01', name: 'rue_de_vivre', email });
            setUsername('rue_de_vivre');
            setArtistData({ id: 'rdv01', name: 'rue_de_vivre', email });
          }
          
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.log('No existing authentication found:', error);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
    
    // Handle tab visibility changes - validate token when user returns to tab
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        console.log('👁️ Tab became visible, validating session...');
        const storedToken = localStorage.getItem('cognito_token');
        
        if (storedToken) {
          try {
            const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            
            if (tokenPayload.exp && tokenPayload.exp <= currentTime) {
              console.log('⏰ Token expired while away, logging out...');
              signOut();
            } else {
              console.log('✅ Session still valid');
            }
          } catch (error) {
            console.error('Error validating token on tab focus:', error);
            signOut();
          }
        } else {
          console.log('❌ No token found, logging out...');
          signOut();
        }
      }
    };
    
    // Add event listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Set up periodic token validation (every 5 minutes)
    const tokenValidationInterval = setInterval(() => {
      const storedToken = localStorage.getItem('cognito_token');
      if (storedToken && isAuthenticated) {
        try {
          const tokenPayload = JSON.parse(atob(storedToken.split('.')[1]));
          const currentTime = Math.floor(Date.now() / 1000);
          const timeUntilExpiry = tokenPayload.exp - currentTime;
          
          // If token expires in less than 5 minutes, log out
          if (timeUntilExpiry < 300) {
            console.log('⏰ Token expiring soon, logging out...');
            signOut();
          }
        } catch (error) {
          console.error('Error checking token expiration:', error);
          signOut();
        }
      }
    }, 300000); // Check every 5 minutes
    
    // Cleanup interval and event listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (tokenValidationInterval) {
        clearInterval(tokenValidationInterval);
      }
    };
  }, []); // Remove isAuthenticated dependency to prevent loops

  const signIn = async (email, password) => {
    setLoading(true);
    try {
      const result = await CognitoAuthService.signIn(email, password);
      
      if (result.success) {
        // Handle admin user separately
        if (email === 'ops@decodedmusic.com') {
          setUser({ email, name: 'Administrator', role: 'admin', isAdmin: true });
          setUsername('Administrator');
          setArtistData(null);
          setIsAuthenticated(true);
        } else {
          // Set up user data based on successful authentication
          await setArtistIdFromUser();
          
          // Fetch artist data based on email
          try {
            const artistResult = await getArtistData(null, email);
            if (artistResult && artistResult.artist) {
              const artist = artistResult.artist;
              setUser({ artistId: artist.id, name: artist.name, email });
              setUsername(artist.name);
              setArtistData(artist);
            } else {
              // Fallback to default if artist not found
              setUser({ artistId: 'rdv01', name: 'rue_de_vivre', email });
              setUsername('rue_de_vivre');
              setArtistData({ id: 'rdv01', name: 'rue_de_vivre', email });
            }
          } catch (artistError) {
            console.warn('Could not fetch artist data, using defaults:', artistError);
            setUser({ artistId: 'rdv01', name: 'rue_de_vivre', email });
            setUsername('rue_de_vivre');
            setArtistData({ id: 'rdv01', name: 'rue_de_vivre', email });
          }
          
          setIsAuthenticated(true);
        }
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message || 'Authentication failed' };
    } finally {
      setLoading(false);
    }
  };

    const signOut = async () => {
    console.log('🚪 Signing out user...');
    
    try {
      // Clear Cognito session via SDK
      await CognitoAuthService.signOut();
    } catch (error) {
      console.warn('Error clearing Cognito session:', error);
    }
    
    // Clear localStorage tokens
    localStorage.removeItem('cognito_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('user_data');
    
    // Clear sessionStorage as well
    sessionStorage.clear();
    
    // Clear all local state
    setUser(null);
    setUsername('');
    setArtistData(null);
    setIsAuthenticated(false);
    setLoading(false);
    
    console.log('✅ User signed out successfully');
    
    // Redirect to welcome page
    window.location.href = '/welcome';
  };

  return (
    <AuthContext.Provider value={{ user, username, artistData, isAuthenticated, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

